import React, { useState } from "react";
import { AppProvider, useApp } from "./context/AppContext";
import { User, Role } from "./types";

// Layout
import Sidebar from "./components/layout/Sidebar";
import TopBar from "./components/layout/TopBar";

// Pages
import LoginPage from "./pages/LoginPage";

// Super admin pages
import SuperAdminDashboard from "./pages/superadmin/Dashboard";
import OrganizationsPage from "./pages/superadmin/OrganizationsPage";
import UsersPage from "./pages/superadmin/UsersPage";
import TraitsPage from "./pages/superadmin/TraitsPage";
import ScalePage from "./pages/superadmin/ScaleManage";
import Assessment from "./pages/superadmin/AssessmentTemplatesPage";
import SectionsPage from "./pages/superadmin/SectionsPage";
import QuestionsType from "./pages/superadmin/QuestionTypeManagementPage";
import QuestionsPage from "./pages/superadmin/QuestionsPage";
import SAAssignmentsPage from "./pages/superadmin/AssignmentsPage";
import ResultsPage from "./pages/superadmin/ResultsPage";
import ReportsPage from "./pages/superadmin/ReportsPage";
import SettingsPage from "./pages/superadmin/SettingsPage";

// Org admin pages
import OrgAdminDashboard from "./pages/orgadmin/Dashboard";
import OrgAssesmentExam from "./pages/orgadmin/OrgAssessmentPage";
import CandidatesPage from "./pages/orgadmin/CandidatesPage";
import OrgAssignmentsPage from "./pages/orgadmin/AssignmentsPage";

// Candidate pages
import CandidateDashboard from "./pages/candidate/Dashboard";
import MyExamsPage from "./pages/candidate/MyExamsPage";
import ExamTakingPageWrapper from "./pages/candidate/ExamTakingPageWrapper";
import ScoringRulesPage from "pages/superadmin/ScoringRulesPage";
import IndexWarning from "components/ui/IndexWarning";
import TraitCategoriesPage from "pages/superadmin/TraitCategoriesPage";

const InnerApp: React.FC = () => {
  const { currentUser, setCurrentUser } = useApp();
  const [activeTab, setTab] = useState("dashboard");
  const [collapsed, setCol] = useState(false);

  const handleLogin = (u: User) => {
    setCurrentUser(u);
    setTab("dashboard");
  }; 

  const handleLogout = () => {
    setCurrentUser(null);
    setTab("dashboard");
  };

  if (!currentUser) return <LoginPage onLogin={handleLogin} />;

  const role = currentUser.role;

  const renderPage = () => {
    if (role === "super_admin") {
      switch (activeTab) {
        case "dashboard": return <SuperAdminDashboard onNavigate={setTab} />;
        case "organizations": return <OrganizationsPage />;
        case "users": return <UsersPage />;
        case "trait-categories": return <TraitCategoriesPage />;
        case "traits": return <TraitsPage />;
        case "scale": return <ScalePage />;
        case "sections": return <SectionsPage />;
        case "question-type": return <QuestionsType />;
        case "questions": return <QuestionsPage />;
        case "scoring_rules": return <ScoringRulesPage />;
        case "manage_assessment_templates": return <Assessment />;
        case "assignments": return <SAAssignmentsPage />;
        case "results": return <ResultsPage />;
        case "reports": return <ReportsPage />;
        case "settings": return <SettingsPage />;
        default: return <SuperAdminDashboard onNavigate={setTab} />;
      }
    }

    if (role === "org_admin") {
      switch (activeTab) {
        case "dashboard": return <OrgAdminDashboard orgId={currentUser.orgId} onNavigate={setTab} />;
        case "candidates": return <CandidatesPage orgId={currentUser.orgId} />;
        case "assessment": return <OrgAssesmentExam orgId={currentUser.orgId} />;
        case "assignments": return <OrgAssignmentsPage orgId={currentUser.orgId} />;
        case "results": return <ResultsPage />;
        case "reports": return <ReportsPage />;
        default: return <OrgAdminDashboard orgId={currentUser.orgId} onNavigate={setTab} />;
      }
    }

    if (role === "candidate") {
      switch (activeTab) {
        case "dashboard": return <CandidateDashboard user={currentUser} onNavigate={setTab} />;
        case "exams": return <MyExamsPage user={currentUser} onNavigate={setTab} />;
        case "exam_start": return <ExamTakingPageWrapper />;
        case "results": return <ResultsPage candidateId={currentUser.id} />;
        case "reports": return <ReportsPage />;
        default: return <CandidateDashboard user={currentUser} onNavigate={setTab} />;
      }
    }

    return null;
  };

  const ml = collapsed ? "ml-16" : "ml-64";

  return (
    <div className="app-bg min-h-screen">
      <div className="pointer-events-none fixed inset-0 overflow-hidden z-0">
        <div className="absolute -top-60 -left-40 w-[500px] h-[500px] rounded-full opacity-20"
          style={{ background: "radial-gradient(circle,#a5b4fc,transparent)" }} />
        <div className="absolute top-1/2 -right-60 w-[400px] h-[400px] rounded-full opacity-15"
          style={{ background: "radial-gradient(circle,#6ee7b7,transparent)" }} />
        <div className="absolute -bottom-40 left-1/3 w-[350px] h-[350px] rounded-full opacity-10"
          style={{ background: "radial-gradient(circle,#fde68a,transparent)" }} />
      </div>

      <Sidebar
        role={role}
        activeTab={activeTab}
        onTabChange={setTab}
        collapsed={collapsed}
        onCollapse={() => setCol(c => !c)}
      />

      <TopBar user={currentUser} onLogout={handleLogout} collapsed={collapsed} />

      <main className={`${ml} pt-16 transition-all duration-300 relative z-10`}>
        <div className="p-6 max-w-7xl mx-auto">
          {renderPage()}
        </div>
      </main>
    </div>
  );
};

const App: React.FC = () => (
  <AppProvider>
    <InnerApp />
  </AppProvider>
);

export default App;